import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from OAuth callback — let AuthCallback exchange the session first.
    if (window.location.hash?.includes("session_id=")) {
      setReady(true);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  // Exchange the one-time session_id for a persistent session cookie.
  const processSession = useCallback(async (sessionId) => {
    const { data } = await api.post("/auth/session", null, { headers: { "X-Session-ID": sessionId } });
    setUser(data);
    setReady(true);
    return data;
  }, []);

  const loginWithGoogle = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed:", e);
    }
    setUser(false);
  }, []);

  const value = useMemo(() => {
    const isSuperadmin = user && user.role === "superadmin";
    const isAdmin = user && (user.role === "admin" || user.role === "superadmin");
    const isActive = user && user.status === "active";
    const perms = (user && user.permissions) || { companies: [], doc_types: [], can_edit: false };
    const inScope = (companyId, type) => {
      if (isAdmin) return true;
      const okC = !perms.companies?.length || perms.companies.includes(companyId);
      const okT = !perms.doc_types?.length || perms.doc_types.includes(type);
      return okC && okT;
    };
    return {
      user,
      ready,
      setUser,
      processSession,
      loginWithGoogle,
      logout,
      isAdmin,
      isSuperadmin,
      isActive,
      perms,
      canEditAny: !!(isAdmin || perms.can_edit),
      canEditDoc: (companyId, type) => !!(isAdmin || (perms.can_edit && inScope(companyId, type))),
      refresh: checkAuth,
    };
  }, [user, ready, processSession, loginWithGoogle, logout, checkAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
